<template>
  <div class="search-widget">
    <b-input-group class>
      <label for="header-search" class="sr-only">{{ $t('heading') }}</label>
      <b-form-input id="header-search" :placeholder="$_t('KW:search')" v-model="query"></b-form-input>
      <b-input-group-text :slot="isRTL ? 'prepend' : 'append'">
        <i class="fa fa-search"></i>
      </b-input-group-text>
    </b-input-group>

    <div class="search-preview container position-absolute" v-if="query.length >= 4 || showKeyboard">
      <div>
        <button type="button" class="close" aria-label="Close" @click="close">
          <span aria-hidden="true" class="font-weight-bold">X</span>
        </button>
        <div class="row mb-3" v-if="showKeyboard">
          <div class="col">
            <keyboard v-model="query" :layouts="keyboardLayouts"></keyboard>
          </div>
        </div>
        <div class="row">
          <div class="col mb-3 text-size-normal" v-if="!loading">
            {{ $t('found', { count: totalResults }) }}
            <strong class="text-primary">{{ query }}</strong>

            <div v-if="totalResults > 25">
              {{ $t('showPerPage') }}
              <span class="btn-group">
                <button v-for="i in [25, 50, 100]" :key="i" class="btn btn-sm btn-primary" :class="perPage == i ? 'active' : ''" @click="changePerPage(i)">
                  {{ i }}
                </button>
              </span>
            </div>
          </div>
        </div>
        <div class="row px-3" v-if="query.length >= 1">
          <div class="col">
            <b-tabs>
              <b-tab v-if="data.shasShiurim && data.shasShiurim.count">
                <template slot="title">
                  {{ $t('shasShiurim') }}
                  <b-badge variant="primary">{{ data.shasShiurim.count }}</b-badge>
                </template>
                <ul class="list-unstyled">
                  <li v-for="shiur in data.shasShiurim.result" :key="shiur.id" class="py-2 pl-2 border-bottom border-light d-flex align-items-center row">
                    <div class="col-12 col-md text-center text-sm-left">
                      <strong>
                        <router-link :to="{ name: 'shiur-single', params: { masechta: shiur.masechta.url_key, daf: shiur.daf } }">
                          <highlight :msg="shiur.masechta.name" :search="query" effect="highlight" />
                          {{ shiur.daf }}
                        </router-link>
                      </strong>
                      <br />by
                      <router-link :to="{ name: 'magideiShiur', params: { slug: shiur.lecturer.url_key } }">{{ shiur.lecturer.name }}</router-link>
                      <div v-if="searchedNote(shiur.note)" classs="w-100 d-block">
                        <highlight :msg="searchedNote(shiur.note)" :search="query" effect="highlight" />
                      </div>
                    </div>
                    <div class="col-12 col-md-3">
                      <action-buttons type="shas" :shiurim="shiur" :magidShiur="shiur.lecturer" :hide-text="true" />
                    </div>
                  </li>
                </ul>
                <b-pagination
                  v-if="data.shasShiurim.count > this.perPage"
                  class="mt-1"
                  align="center"
                  size="sm"
                  :total-rows="data.shasShiurim.count"
                  v-model="page.shasShiurim"
                  :per-page="perPage"
                  @change="paginateShas"
                ></b-pagination>
              </b-tab>

              <b-tab v-if="data.mishnaShiurim && data.mishnaShiurim.count">
                <template slot="title">
                  {{ $t('mishnaShiurim') }}
                  <b-badge variant="primary">{{ data.mishnaShiurim.count }}</b-badge>
                </template>
                <ul class="list-unstyled">
                  <li v-for="shiur in data.mishnaShiurim.result" :key="shiur.id" class="py-2 pl-2 border-bottom border-light d-flex align-items-center row">
                    <div class="col-12 col-md text-center text-sm-left">
                      <strong>
                        <router-link
                          :to="{
                            name: 'mishna-shiur-single',
                            params: {
                              book: shiur.book.url_key,
                              perek: shiur.mishna_chapter,
                              mishna: shiur.mishna_number,
                            },
                          }"
                        >
                          <highlight :msg="shiur.book.name" :search="query" effect="highlight" />
                          {{ shiur.mishna_chapter }} {{ shiur.mishna_number }}
                        </router-link>
                      </strong>
                      <br />by
                      <router-link :to="{ name: 'magideiShiurMishna', params: { slug: shiur.professor.url_key } }">{{ shiur.professor.name }}</router-link>
                      <div v-if="searchedNote(shiur.note)" classs="w-100 d-block">
                        <highlight :msg="searchedNote(shiur.note)" :search="query" effect="highlight" />
                      </div>
                    </div>
                    <div class="col-12 col-md-3">
                      <action-buttons type="mishna" :shiurim="shiur" :magidShiur="shiur.professor" :hide-text="true" />
                    </div>
                  </li>
                </ul>
                <b-pagination
                  v-if="data.mishnaShiurim.count > this.perPage"
                  class="mt-1"
                  align="center"
                  size="sm"
                  :total-rows="data.mishnaShiurim.count"
                  v-model="page.mishnaShiurim"
                  :per-page="perPage"
                  @change="paginateMishna"
                ></b-pagination>
              </b-tab>

              <b-tab v-if="data.masechtos && data.masechtos.count">
                <template slot="title">
                  {{ $_t('KW:masechtos') }}
                  <b-badge variant="primary">{{ data.masechtos.count }}</b-badge>
                </template>
                <masechtos-list :query="query" :masechtos="data.masechtos.result" />
                <b-pagination
                  v-if="data.masechtos.count > this.perPage"
                  class="mt-1"
                  align="center"
                  size="sm"
                  :total-rows="data.masechtos.count"
                  v-model="page.masechtos"
                  :per-page="perPage"
                  @change="paginateMasechtos"
                ></b-pagination>
              </b-tab>
              <b-tab v-if="data.books && data.books.count">
                <template slot="title">
                  {{ $_t('KW:mishnaMasechta') }}
                  <b-badge variant="primary">{{ data.books.count }}</b-badge>
                </template>
                <book-list :query="query" :books="data.books.result" />
                <b-pagination
                  v-if="data.books.count > this.perPage"
                  class="mt-1"
                  align="center"
                  size="sm"
                  :total-rows="data.books.count"
                  v-model="page.books"
                  :per-page="perPage"
                  @change="paginateBooks"
                ></b-pagination>
              </b-tab>
              <b-tab v-if="data.magideiShiur && data.magideiShiur.count">
                <template slot="title">
                  {{ $_t('KW:magideiShiur') }}
                  <b-badge variant="primary">{{ data.magideiShiur.count }}</b-badge>
                </template>
                <magidei-list colXL="6" :magidei-shiur="data.magideiShiur.result" :query="query" type="shas"></magidei-list>
                <b-pagination
                  v-if="data.magideiShiur.count > this.perPage"
                  class="mt-1"
                  align="center"
                  size="sm"
                  :total-rows="data.magideiShiur.count"
                  v-model="page.magideiShiur"
                  :per-page="perPage"
                  @change="paginateMagidei"
                ></b-pagination>
              </b-tab>
              <b-tab v-if="data.topics && data.topics.count">
                <template slot="title">
                  {{ $_t('KW:topics') }}
                  <b-badge variant="primary">{{ data.topics.count }}</b-badge>
                </template>
                <topic-list :topics="data.topics.result" :query="query"></topic-list>
                <b-pagination
                  v-if="data.topics.count > this.perPage"
                  class="mt-1"
                  align="center"
                  size="sm"
                  :total-rows="data.topics.count"
                  v-model="page.topics"
                  :per-page="perPage"
                  @change="paginateTopics"
                ></b-pagination>
              </b-tab>
              <b-tab v-if="data.torahShiurim && data.torahShiurim.count">
                <template slot="title">
                  {{ $_t('KW:torahShiurim') }}
                  <b-badge variant="primary">{{ data.torahShiurim.count }}</b-badge>
                </template>
                <ul class="list-unstyled">
                  <li
                    v-for="shiur in data.torahShiurim.result"
                    :key="shiur.id"
                    class="py-2 pl-3 border-bottom border-light d-flex align-items-center row text-center text-sm-left"
                  >
                    <div class="col-12 col-md text-center text-sm-left">
                      <router-link :to="{ name: 'torah-shiur-single', params: { id: shiur.id } }">
                        <highlight :msg="shiur.name" :search="query" effect="highlight" />
                      </router-link>
                      <br />by
                      <router-link :to="{ name: 'magideiShiurTorah', params: { slug: shiur.speaker.url_key } }">{{ shiur.speaker.name }}</router-link>
                      <div v-if="searchedNote(shiur.note)" classs="w-100 d-block">
                        <highlight :msg="searchedNote(shiur.note)" :search="query" effect="highlight" />
                      </div>
                    </div>
                    <div class="col-12 col-md-3">
                      <action-buttons type="torah" :shiurim="shiur" :magidShiur="shiur.speaker" :hide-text="true" />
                    </div>
                  </li>
                </ul>
                <b-pagination
                  v-if="data.torahShiurim.count > this.perPage"
                  class="mt-1"
                  align="center"
                  size="sm"
                  :total-rows="data.torahShiurim.count"
                  v-model="page.torahShiurim"
                  :per-page="perPage"
                  @change="paginateTorah"
                ></b-pagination>
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
$(window).click(function () {});

$('.search-preview').click(function (event) {
  event.stopPropagation();
});
</script>
<script>
import keyboard from 'vue-keyboard';
import ActionButtonsComponent from './ActionButtons.vue';
import magideiListComponent from '../magideiShiur/magideiList.vue';
import topicsListComponent from './SearchWidget/Topics.vue';
import MasechtosComponent from './SearchWidget/Masechtos.vue';
import BookComponent from './SearchWidget/Books.vue';

import HighlightComponent from './SearchWidget/Highlight.vue';
import { debounce } from 'lodash';
export default {
  components: {
    keyboard: keyboard,
    actionButtons: ActionButtonsComponent,
    magideiList: magideiListComponent,
    topicList: topicsListComponent,
    masechtosList: MasechtosComponent,
    bookList: BookComponent,
    Highlight: HighlightComponent,
  },
  i18nOptions: {
    namespaces: 'components',
    keyPrefix: 'search',
  },
  data: function () {
    return {
      loading: false,
      perPage: 50,
      query: '',
      page: {
        torahIlluminated: 0,
        shasIlluminated: 0,
        masechtos: 0,
        topics: 0,
        magideiShiur: 0,
      },
      showKeyboard: false,
      keyboardLayouts: [
        "~1234567890-={←:backspace}|/'קראטוןםפ\\|שדגכעיחלךף,][|{shift:goto:1}זסבהנמצתץ.{shift:goto:1}|{space:space}",
        '    ₪        {←:backspace}|  €   װ    |     ײױ      |{shift:goto:0}          {shift:goto:0}|{space:space}',
      ],
      data: {},
    };
  },
  computed: {
    totalResults: function () {
      if (this.data.masechtos) {
        return (
          this.data.masechtos.count +
          this.data.torahShiurim.count +
          this.data.shasShiurim.count +
          this.data.books.count +
          this.data.mishnaShiurim.count +
          this.data.magideiShiur.count +
          this.data.topics.count
        );
      }
      return 0;
    },
  },
  watch: {
    $route: function () {
      this.query = '';
      this.showKeyboard = false;
    },
    perPage: function (val, oldVal) {
      if (val !== oldVal) {
        this.getData();
      }
    },
    query: debounce(function (val, oldVal) {
      console.log('called');
      if (val.length > 3) {
        return this.getData();
      }
      return (this.data = {});
    }, 400),
  },
  methods: {
    getData: function () {
      //this.data = {};
      this.loading = true;
      this.$http.get('/api/search/' + this.query + '/' + this.perPage).then(response => {
        this.data = response.data;
        this.page = {
          torahIlluminated: 1,
          shasIlluminated: 1,
          masechtos: 1,
          topics: 1,
          magideiShiur: 1,
        };
        this.loading = false;
      });
    },
    paginateMasechtos: function (page) {
      this.getPaginatedData('masechtos', page);
    },
    paginatebooks: function (page) {
      this.getPaginatedData('books', page);
    },
    paginateTopics: function (page) {
      this.getPaginatedData('topics', page);
    },
    paginateMagidei: function (page) {
      this.getPaginatedData('magideiShiur', page);
    },
    paginateTorah: function (page) {
      this.getPaginatedData('torahShiurim', page);
    },
    paginateShas: function (page) {
      this.getPaginatedData('shasShiurim', page);
    },
    paginateMishna: function (page) {
      this.getPaginatedData('mishnaShiurim', page);
    },
    getPaginatedData: function (type, page) {
      this.loading = true;
      this.$http.get('/api/search/' + type + '/' + this.query + '/' + (page - 1) * this.perPage + '/' + this.perPage).then(response => {
        this.data[type] = response.data;
        this.loading = false;
      });
    },
    close: function () {
      this.query = '';
    },
    changePerPage: function (val) {
      this.perPage = val;
    },
    slug: function (val) {
      if (val) {
        var result = val
          .toString()
          .toLowerCase()
          .replace(/\s+/g, '-') // Replace spaces with -
          .replace(/[^\w\-]+/g, '') // Remove all non-word chars
          .replace(/\-\-+/g, '-') // Replace multiple - with single -
          .replace(/^-+/, '') // Trim - from start of text
          .replace(/-+$/, ''); // Trim - from end of text
        if (!result || 0 === result.length) {
          return 'hebrew';
        }
        return result;
      }
      return 'not-specified';
    },
    searchedNote: function (note) {
      var re = new RegExp('(?:\\S+\\s*){0,5}\\S*' + this.query + '\\S*(?:\\s*\\S+){0,5}', 'ig');
      var match = note.toString().match(re);
      if (match) {
        return match.join(' [...] ');
      }
    },
  },
};
</script>

<style lang="scss">
.search-widget {
  #header-search {
    border-radius: 20px 0 0 20px;
    border-right: none;
    padding-left: 20px;
  }
  .input-group-append .input-group-text {
    border-radius: 0 20px 20px 0;
    border-left: none;
    background: none;

    .fa {
      font-size: 1.1rem;
    }
  }
  .search-preview {
    background: white;
    z-index: 1000;
    min-width: 50vw;
    right: 20px;
    // top: 40px;
    padding: 20px;
    margin-top: 10px;
    max-height: 80vh;
    overflow-y: scroll;
    // box-shadow: 0px 8px 10px #e2e2e2, -10px 8px 15px #e2e2e2,
    //     10px 8px 15px #e2e2e2;
  }
}
</style>
